import React, { memo, useEffect, useState } from 'react';

import { useIsFocused } from '@react-navigation/native';

import {
  useEnableNativeTabBarExperiment,
  useExposeNativeTabBarExperiment,
} from 'experiments/use-enable-native-tab-bar-experiment';
import ClassicTabsLayout from 'navigation/layouts/classic-tabs-layout';
import NativeTabsLayout from 'navigation/layouts/native-tabs-layout';

function TabsLayoutGate() {
  const isTabsFocused = useIsFocused();
  const {
    isReady,
    isTreatment,
    hasResolvedVariant,
    isPlatformEligible,
  } = useEnableNativeTabBarExperiment();

  // iOS 26's native Liquid Glass tab bar permanently corrupts its label-width layout (only a
  // tab press fixes it) if its first native attach happens while the "(tabs)" screen isn't
  // focused - e.g. on a deep link that pushes a screen on top of it before this screen ever
  // becomes the visible one. So NativeTabsLayout's first mount specifically requires LIVE focus,
  // not just "was focused at some earlier point" - the experiment can resolve to treatment while
  // a later full-screen route happens to be covering tabs, and in that case it must keep waiting.
  // `hasNativeTabsMounted` then latches that decision so a later blur (e.g. opening a cart modal)
  // never unmounts the already-attached tab bar and resets its navigator state.
  const [hasNativeTabsMounted, setHasNativeTabsMounted] = useState(false);
  const shouldRenderNativeTabs = isTreatment && (hasNativeTabsMounted || isTabsFocused);

  useEffect(() => {
    if (shouldRenderNativeTabs) {
      setHasNativeTabsMounted(true);
    }
  }, [shouldRenderNativeTabs]);

  // An ineligible platform (Android, or iOS below the Liquid Glass minimum) can never render
  // treatment, so an assignment there must not count as exposure. The tabs route can also be
  // mounted underneath a full-screen deep-linked route such as /menu/section/:id; only expose
  // while the tabs navigator is CURRENTLY focused and its tab bar is actually visible - a screen
  // that covered tabs before this experiment resolved must not count as exposure just because
  // tabs were focused at some earlier point.
  // `isTreatment` and `hasResolvedVariant` are already locked for the session by the hook itself
  // once resolved (see use-enable-native-tab-bar-experiment.ts), so this exposure call stays
  // consistent with whichever navigator is actually mounted below, even across later identity
  // changes.
  useExposeNativeTabBarExperiment(isTabsFocused && hasResolvedVariant && isPlatformEligible);

  // Don't mount either navigator until the experiment is resolved - ineligible platforms are
  // ready immediately (see resolveNativeTabBarExperimentGate), so this only briefly delays
  // eligible iOS devices while Amplitude loads. Mounting ClassicTabsLayout first and swapping
  // to NativeTabsLayout once treatment resolves would flash the classic tab bar and reset
  // navigator state instead.
  if (!isReady) {
    return null;
  }

  if (isTreatment && !shouldRenderNativeTabs) {
    return null;
  }

  return isTreatment ? <NativeTabsLayout /> : <ClassicTabsLayout />;
}

export default memo(TabsLayoutGate);
